import React, { useState, useEffect } from "react";
import { motion, Variants } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import AuthBranding, { AuthView } from "@/components/auth/AuthBranding";
import AuthLogin from "@/components/auth/AuthLogin";
import AuthRegister from "@/components/auth/AuthRegister";
import AuthForgotPassword from "@/components/auth/AuthForgotPassword";
import SubscriptionPlans from "./SubscriptionPlans";
import { useTranslation } from "react-i18next";

const Auth: React.FC = () => {
    const { login, createUserAuth, registerWithToken, loginWithProvider, forgotPassword } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { token } = useParams<{ token: string }>();
    const hasToken = !!token;

    // Anche con un invito si parte dall'accesso: chi è invitato quasi sempre un account
    // ce l'ha già, e aprire la registrazione lo portava a crearne un secondo.
    const [view, setView] = useState<AuthView>("login");
    const [loading, setLoading] = useState(false);
    const [registrationSuccess, setRegistrationSuccess] = useState(false);
    const [forgotPasswordSent, setForgotPasswordSent] = useState(false);

    // Validazione anticipata del token invito: un invito scaduto/usato non deve
    // far proseguire la registrazione (che senza token creerebbe un nuovo salone da admin).
    const [inviteStatus, setInviteStatus] = useState<'checking' | 'valid' | 'invalid' | null>(hasToken ? 'checking' : null);
    const [inviteShopName, setInviteShopName] = useState<string | null>(null);

    useEffect(() => {
        let active = true;
        if (!hasToken || !token) return;
        supabase.rpc('check_invite_status', { p_token: token }).then(({ data, error }: any) => {
            if (!active) return;
            if (error || !data?.valid) {
                setInviteStatus('invalid');
                setView('login');
                toast.error(
                    data?.used
                        ? t('auth.invite.used', 'Questo invito è già stato utilizzato. Chiedi al titolare di inviartene uno nuovo.')
                        : t('auth.invite.invalid', 'Invito non valido o scaduto. Chiedi al titolare di inviartene uno nuovo.'),
                    { duration: 10000 }
                );
            } else {
                setInviteStatus('valid');
                setInviteShopName(data?.shop_name ?? null);
            }
        });
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasToken, token]);

    // Clear errors when switching views
    useEffect(() => {
        setRegistrationSuccess(false);
        setForgotPasswordSent(false);
    }, [view]);

    // LOGIN
    const handleLogin = async (data: { email: string; password: string }) => {
        setLoading(true);
        // Se c'è un token (invito), lo passiamo al login service che gestirà l'onboarding
        const res = await login({
            email: data.email,
            password: data.password,
            inviteToken: hasToken && token ? token : undefined
        });
        setLoading(false);

        if (!res.success) {
            toast.error(res.error || t('auth.toasts.login_error'));
        } else {
            toast.success(t('auth.toasts.login_success'));
            // Explicit navigation based on result or default to home
            if (res.redirectTo) {
                navigate(res.redirectTo);
            } else {
                navigate('/');
            }
        }
    };

    // REGISTER
    const handleRegister = async (data: {
        name: string;
        email: string;
        password: string;
        termsAccepted: boolean;
        privacyAccepted: boolean;
        marketingAccepted?: boolean;
    }) => {
        setLoading(true);

        const trimmed = data.name.trim();
        const [firstName, ...rest] = trimmed.split(" ");
        const lastName = rest.join(" ") || "";

        const userData = {
            email: data.email,
            password: data.password,
            first_name: firstName,
            last_name: lastName,
            accepted_terms: data.termsAccepted,
            privacy_policy_read: data.privacyAccepted,
            consent_marketing: !!data.marketingAccepted,
        };

        let res;
        if (hasToken) {
            if (inviteStatus !== 'valid') {
                setLoading(false);
                toast.error(t('auth.invite.invalid', 'Invito non valido o scaduto. Chiedi al titolare di inviartene uno nuovo.'));
                return;
            }
            res = await registerWithToken(userData, token);
        } else {
            res = await createUserAuth(userData);
        }

        setLoading(false);

        if (!res.success) {
            // GESTIONE UTENTE ESISTENTE
            if (res.error === 'USER_EXISTS_LOGIN_REQUIRED') {
                toast.info(t('auth.toasts.user_exists_title'), {
                    description: t('auth.toasts.user_exists_desc'),
                    duration: 8000,
                });
                setView("login");
            } else {
                // Errore generico
                toast.error(res.error || t('auth.toasts.register_error'));
            }
        } else {
            if (res?.redirectTo === '/new-shop') {
                navigate(res.redirectTo);
            } else {
                setRegistrationSuccess(true);
            }
            toast.success(t('auth.toasts.register_success'));
        }
    };

    // FORGOT PASSWORD
    const handleForgotPassword = async (email: string) => {
        setLoading(true);
        const res = await forgotPassword(email);
        setLoading(false);

        if (!res.success) {
            toast.error(res.error || t('auth.toasts.reset_error'));
            throw new Error(res.error || t('auth.toasts.reset_error'));
        } else {
            toast.success(t('auth.toasts.reset_success'));
        }
    };

    // Clear stale invite data on mount
    useEffect(() => {
        localStorage.removeItem('clip_invite_token');
        localStorage.removeItem('clip_pending_email');
    }, []);

    // GOOGLE
    const handleGoogleLogin = async () => {
        setLoading(true);

        // Se c'è un token nell'URL (invito dipendente) ED è valido, salviamolo per il callback
        if (hasToken && token && inviteStatus === 'valid') {
            localStorage.setItem('clip_invite_token', token);
        }

        const res = await loginWithProvider("google");
        setLoading(false);

        if (!res.success) {
            toast.error(res.error || t('auth.toasts.google_error'));
        }
    };

    const getTitle = () => {
        switch (view) {
            case "login": return t('auth.page.login.title');
            case "register": return t('auth.page.register.title');
            case "forgot_password": return t('auth.page.forgot.title');
            default: return "";
        }
    };

    const getSubtitle = () => {
        switch (view) {
            case "login": return t('auth.page.login.subtitle');
            case "register": return t('auth.page.register.subtitle');
            case "forgot_password": return t('auth.page.forgot.subtitle');
            default: return "";
        }
    };

    const formVariants: Variants = {
        hidden: { opacity: 0, x: 20 },
        visible: { opacity: 1, x: 0, transition: { duration: 0.5, ease: "easeOut" } },
    };

    return (
        <div className="min-h-screen w-full flex flex-col lg:flex-row bg-white font-sans overflow-x-hidden">
            {/* LEFT PANEL */}
            <AuthBranding
                view={view}
                onChangeView={setView}
                onLogoClick={() => navigate("/subscription")}
            />

            {/* RIGHT PANEL */}
            <div className="flex-1 flex flex-col justify-center items-center bg-white p-6 sm:p-12 lg:p-20 relative z-10">
                <div className="max-w-md w-full mx-auto">
                    {/* Niente AnimatePresence: con framer-motion da CDN + React 19 l'uscita
                        non si completava mai e il cambio vista restava bloccato sul form
                        precedente (chi arrivava da un invito non riusciva a registrarsi). */}
                    <div>
                        <motion.div
                            key={view === "register" && registrationSuccess ? "register-success" : view}
                            variants={formVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            {/* REGISTER SUCCESS STATE */}
                            {view === "register" && registrationSuccess ? (
                                <div className="text-center space-y-6">
                                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                        <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <h2 className="text-3xl font-bold text-gray-900">{t('auth.page.success.title')}</h2>
                                    <p className="text-gray-500 text-lg">
                                        {t('auth.page.success.desc')}
                                    </p>
                                    <div className="pt-6 space-y-4">
                                        <button
                                            onClick={() => { setRegistrationSuccess(false); setView("login"); }}
                                            className="w-full py-3.5 bg-gray-900 text-white font-bold rounded-xl hover:bg-black transition-colors shadow-lg shadow-gray-200/50"
                                        >
                                            {t('auth.page.success.back_btn')}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {/* BANNER INVITO: chiarisce che si entra in un salone esistente,
                                        non si crea un nuovo account titolare */}
                                    {hasToken && inviteStatus === 'valid' && view !== "forgot_password" && (
                                        <div className="mb-6 p-4 bg-clip-50 border border-clip-200 rounded-2xl">
                                            <p className="text-sm text-gray-800">
                                                {inviteShopName
                                                    ? t('auth.invite.banner_shop', { name: inviteShopName, defaultValue: `Sei stato invitato a unirti al team di ${inviteShopName}.` })
                                                    : t('auth.invite.banner', 'Sei stato invitato a unirti a un team su Clip.')}
                                            </p>
                                            <p className="text-sm text-gray-500 mt-1">
                                                {view === "login"
                                                    ? t('auth.invite.banner_login_hint', 'Accedi con il tuo account Clip per completare l\'ingresso.')
                                                    : t('auth.invite.banner_register_hint', 'Stai creando un account per entrare nel team: nessun nuovo salone verrà creato.')}
                                            </p>
                                            <button
                                                type="button"
                                                onClick={() => setView(view === "login" ? "register" : "login")}
                                                className="mt-2 text-sm font-bold text-clip-600 hover:text-clip-800 transition-colors"
                                            >
                                                {view === "login"
                                                    ? t('auth.invite.banner_to_register', 'Non hai ancora un account? Creane uno')
                                                    : t('auth.invite.banner_to_login', 'Hai già un account? Accedi')}
                                            </button>
                                        </div>
                                    )}

                                    {/* Hide title if forgot password email is sent */}
                                    {(!forgotPasswordSent || view !== "forgot_password") && (
                                        <div className="mb-8">
                                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2 tracking-tight">
                                                {getTitle()}
                                            </h2>
                                            <p className="text-gray-500 text-sm md:text-base">
                                                {getSubtitle()}
                                            </p>

                                            {/* MOBILE SWITCHER */}
                                            {view !== "forgot_password" && (
                                                <div className="lg:hidden mt-6 p-4 bg-gray-50 rounded-2xl border border-gray-100 flex justify-between items-center">
                                                    <span className="text-sm text-gray-600">
                                                        {view === "login" ? t('auth.page.mobile.no_account') : t('auth.page.mobile.has_account')}
                                                    </span>
                                                    <button
                                                        onClick={() => setView(view === "login" ? "register" : "login")}
                                                        className="text-sm font-bold text-clip-600 hover:text-clip-800 transition-colors bg-white px-3 py-1.5 rounded-lg shadow-sm border border-gray-200"
                                                    >
                                                        {view === "login" ? t('auth.page.mobile.register_btn') : t('auth.page.mobile.login_btn')}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {view === "login" && (
                                        <AuthLogin
                                            loading={loading}
                                            onLogin={handleLogin}
                                            onForgotPassword={() => setView("forgot_password")}
                                        />
                                    )}

                                    {view === "register" && (
                                        <AuthRegister
                                            loading={loading}
                                            onRegister={handleRegister}
                                        />
                                    )}

                                    {view === "forgot_password" && (
                                        <AuthForgotPassword
                                            loading={loading}
                                            onSendReset={handleForgotPassword}
                                            isSent={forgotPasswordSent}
                                            onSuccess={() => setForgotPasswordSent(true)}
                                        />
                                    )}

                                    {/* Google button */}
                                    {view !== "forgot_password" && (
                                        <>
                                            <div className="relative my-8">
                                                <div className="absolute inset-0 flex items-center">
                                                    <div className="w-full border-t border-gray-100" />
                                                </div>
                                                <div className="relative flex justify-center text-sm">
                                                    <span className="px-4 bg-white text-gray-400 font-medium text-xs uppercase tracking-wider">
                                                        {t('auth.page.or_continue')}
                                                    </span>
                                                </div>
                                            </div>

                                            <button
                                                type="button"
                                                onClick={handleGoogleLogin}
                                                disabled={loading}
                                                className="w-full py-3.5 bg-white border border-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all flex items-center justify-center gap-3 group disabled:opacity-50"
                                            >
                                                <svg className="w-5 h-5 group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                                                </svg>
                                                Google
                                            </button>
                                        </>
                                    )}
                                </>
                            )}
                        </motion.div>
                    </div>
                </div>

                <div className="mt-auto pt-8 text-center lg:hidden">
                    <p className="text-xs text-gray-400">© 2024 Clip Management</p>
                </div>
            </div>
        </div>
    );
};

export default Auth;